import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import RatingPage from './pages/RatingPage';
import FeedbackPage from './pages/FeedbackPage';
import ContactPage from './pages/ContactPage';
import SubmissionPage from './pages/SubmissionPage';
import ServiceRatingPage from './pages/ServiceRatingPage';
import ClinicRatingPage from './pages/ClinicRatingPage';
import FollowUpPage from './pages/FollowUpPage';

const config = {
  SERVER_URL: process.env.REACT_APP_SERVER_URL,
};

function App() {
  const [rating, setRating] = useState(0);
  const [doctorRating, setDoctorRating] = useState(0);
  const [CARating, setCARating] = useState(0);
  const [cleanRating, setCleanRating] = useState(0);
  const [waitRating, setWaitRating] = useState(0);
  const [accessRating, setAccessRating] = useState(0);
  const [contact, setContact] = useState(false);
  const [submission, setSubmission] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const [name, setName] = useState('');
  const [number, setNumber] = useState(0);

  const timeoutRef = useRef(null);

  // Timer to return to homepage in inactivity
  useEffect(() => {
    const isOnHomepage = rating === 0 && !contact && !submission;

    const resetTimer = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      if (!isOnHomepage) {
        timeoutRef.current = setTimeout(() => {
          resetToHomepage();
        }, 60000); // 60s
      }
    };

    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetTimer));

    resetTimer(); // Start or reset on load/state change

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      clearTimeout(timeoutRef.current);
    };
  }, [rating, contact, submission]);

  const resetToHomepage = () => {
    setRating(0);
    setDoctorRating(0);
    setCARating(0);
    setCleanRating(0);
    setWaitRating(0);
    setAccessRating(0);
    setName('');
    setNumber(0);
    setContact(false);
    setSubmission(false);
    setFeedback(false);
  };
  
  const handleRating = (value) => {
    setRating(value)
  }

  const navigateContact = () => {
    setContact(true)
  }

  const navigateFeedback = () => {
    setFeedback(true)
  }

  const handleContact = ({name, number}) => {
    setName(name);
    setNumber(number);
    setContact(false);
    setSubmission(true);
  }

  const handleCancelContact = () => {
    setContact(false);
    navigateFeedback();
  }

  const handleComplete = () => {
    const feedback = {
      PatientName: name,
      HPNum: number,
      Rating :rating,
      DoctorRating : doctorRating,
      AssistantRating : CARating,
      CleanlinessRating: cleanRating,
      WaitingTimeRating: waitRating,
      AccessibilityRating: accessRating
    }
    
    submitFeedback(feedback);
    resetToHomepage();
  }

  const submitFeedback = async (feedback) => {
    console.log(feedback);
    await fetch(`${config.SERVER_URL}/api/feedback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(feedback)
    })
    .catch(error => {
      alert('Error:' + error);
      console.error(error);
    });
  };

  const handleDoctorRating = (value) => {
    setDoctorRating(value);
  }

  const handleCARating = (value) => {
    setCARating(value)
  }

  const handleCleanRating = (value) => {
    setCleanRating(value)
  }

  const handleWaitRating = (value) => {
    setWaitRating(value)
  }

  const handleAccessRating = (value) => {
    setAccessRating(value)
  }

  const handleBackFromClinicRating = () => {
    setDoctorRating(0);
    setCARating(0);
  }

  const handleBackFromFollowUp = () => {
    setCleanRating(0);
    setWaitRating(0);
    setAccessRating(0);
  }

  const renderRatingPages = () => {
    if (contact || submission) return null;
  
    const hasDoctorCARating = doctorRating && CARating;
    const hasClinicRatings = cleanRating && waitRating && accessRating;
  
    // For ratings 1–3: go through service → clinic → follow-up
    if (rating >= 1 && rating <= 3) {
      if (hasDoctorCARating && hasClinicRatings) {
        if (feedback){
          return <FeedbackPage
          navigateContact={false}
          handleComplete={handleComplete}
        />;
        }
        return <FollowUpPage handleFollowUp={navigateContact} handleComplete={navigateFeedback} handleBack={handleBackFromFollowUp} />;
      }
      if (hasDoctorCARating) {
        return <ClinicRatingPage
          handleCleanRating={handleCleanRating}
          handleWaitRating={handleWaitRating}
          handleAccessRating={handleAccessRating}
          handleBack={handleBackFromClinicRating}
        />;
      }
      return <ServiceRatingPage
        handleDoctorRating={handleDoctorRating}
        handleCARating={handleCARating}
      />;
    }
  
    // Rating 4: Only service rating required
    if (rating === 4) {
      if (hasDoctorCARating) {
        return <FeedbackPage
          navigateContact={navigateContact}
          handleComplete={handleComplete}
        />;
      }
      return <ServiceRatingPage
        handleDoctorRating={handleDoctorRating}
        handleCARating={handleCARating}
      />;
    }
  
    // Rating 5: Straight to feedback page
    if (rating === 5) {
      return <FeedbackPage
        navigateContact={navigateContact}
        handleComplete={handleComplete}
      />;
    }
  
    // Default = homepage (rating === 0)
    return <RatingPage handleRating={handleRating} />;
  };

  return (
    <div className='flex w-screen height-screen justify-center text-center items-center'>
      {renderRatingPages()}
      {contact && <ContactPage handleContact={handleContact} handleComplete={handleCancelContact} />}
      {submission && <SubmissionPage handleSubmission={handleComplete}/>}
    </div>
  );
}

export default App;
